// Simple passthrough - don't modify images on frontend
// The backend will handle any necessary orientation fixes
export const fixImageOrientation = async (file) => {
  console.log('📸 Uploading image without frontend modifications:', file.name);
  return Promise.resolve(file);
};

/**
 * Detects if a URL is an expired R2 presigned URL
 * @param {string} url - The URL to check
 * @returns {boolean} - True if URL appears to be an expired R2 presigned URL
 */
export const isExpiredR2Url = (url) => {
  if (!url || typeof url !== 'string') return false;
  
  // Check for R2 cloudflare storage URLs with presigned parameters
  const hasR2Domain = url.includes('r2.cloudflarestorage') || url.includes('.r2.cloudflarestorage.com');
  const hasPresignedParams = url.includes('X-Amz-') || url.includes('Expires=');
  
  return hasR2Domain && hasPresignedParams;
};

/**
 * Converts expired R2 presigned URLs to backend proxy format (/api/files/{id})
 * Handles Cloudflare CDN URLs properly
 * Falls back to original URL if no valid ID is found
 * @param {string} url - The potentially expired R2 URL
 * @param {string} assetId - The asset ID to use for conversion
 * @returns {string} - Converted URL or original if not expired/no ID
 */
export const convertExpiredR2Url = (url, assetId) => {
  // If no URL provided, return empty string
  if (!url) return '';
  
  // Get backend URL from runtime-safe helper
  try {
    // Lazy import to avoid circular deps at build time
    const { getAPI } = require('./config');
    var API_URL = getAPI();
  } catch (e) {
    var API_URL = (window && window._env_ && window._env_.REACT_APP_BACKEND_URL) ? `${window._env_.REACT_APP_BACKEND_URL}/api` : '';
  }
  
  // If it's a Cloudflare CDN URL, return as-is (no conversion needed)
  if (url.includes('imagedelivery.net') || 
      url.includes('cloudflarestream.com') || 
      url.includes('customer-')) {
    return url;
  }
  
  // If already using /api/files/ format with full URL, return as-is
  if (url.includes('/api/files/') && url.startsWith('http')) return url;
  
  // If expired R2 URL and we have an asset ID, convert to /api/files/ format
  if (isExpiredR2Url(url) && assetId) {
    if (localStorage.getItem('debugMM') === '1') console.log(`🔄 Converting expired R2 URL to ${API_URL}/files/${assetId}`);
    return `${API_URL}/files/${assetId}`;
  }
  
  // If it's a relative /api/files/ URL, prepend the backend URL
  if (url.startsWith('/api/files/')) {
    return `${API_URL}${url.replace('/api', '')}`;
  }
  
  // Otherwise return original URL
  return url;
};

/**
 * Gets the best available image URL, prioritizing Cloudflare CDN URLs
 * @param {object} asset - Asset object with cloudflare_delivery_url, url, thumbnail_url, r2_url, id fields
 * @param {boolean} preferThumbnail - Whether to prefer thumbnail over full resolution
 * @returns {string} - Best available image URL
 */
export const getSafeImageUrl = (asset, preferThumbnail = true) => {
  if (!asset) return '';
  
  const { id, cloudflare_delivery_url, thumbnail_url, r2_url, url } = asset;
  
  // Priority 1: Direct Cloudflare CDN URL (fastest, most reliable)
  if (cloudflare_delivery_url && 
      (cloudflare_delivery_url.includes('imagedelivery.net') || 
       cloudflare_delivery_url.includes('cloudflarestream.com') ||
       cloudflare_delivery_url.includes('customer-'))) {
    return cloudflare_delivery_url;
  }
  
  // Priority 2: Check if url/thumbnail_url are already Cloudflare CDN URLs
  const checkUrl = preferThumbnail ? thumbnail_url : url;
  if (checkUrl && 
      (checkUrl.includes('imagedelivery.net') || 
       checkUrl.includes('cloudflarestream.com') ||
       checkUrl.includes('customer-'))) {
    return checkUrl;
  }
  
  // Priority 3: Use url or thumbnail_url (may be /api/files/ proxy)
  let selectedUrl = preferThumbnail 
    ? (thumbnail_url || url || r2_url)
    : (url || thumbnail_url || r2_url);
  
  // Convert expired R2 URLs to /api/files/ proxy if needed
  return convertExpiredR2Url(selectedUrl, id);
};
